import * as React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { testimonials } from '@/content/homepage';
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion';
import { cn } from '@/lib/utils';

const AUTO_ADVANCE_MS = 9000;

export function TestimonialCarousel() {
  const reducedMotion = usePrefersReducedMotion();
  const [index, setIndex] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const regionRef = React.useRef<HTMLDivElement | null>(null);

  const count = testimonials.length;
  const active = testimonials[index] ?? testimonials[0];

  const goTo = React.useCallback(
    (next: number) => {
      setIndex(((next % count) + count) % count);
    },
    [count]
  );

  const goPrev = React.useCallback(() => goTo(index - 1), [goTo, index]);
  const goNext = React.useCallback(() => goTo(index + 1), [goTo, index]);

  React.useEffect(() => {
    if (reducedMotion || paused || count < 2) return;
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % count);
    }, AUTO_ADVANCE_MS);
    return () => window.clearInterval(timer);
  }, [count, paused, reducedMotion]);

  React.useEffect(() => {
    const node = regionRef.current;
    if (!node) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goPrev();
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goNext();
      }
    };

    node.addEventListener('keydown', onKeyDown);
    return () => node.removeEventListener('keydown', onKeyDown);
  }, [goNext, goPrev]);

  if (!active) return null;

  return (
    <section
      aria-labelledby="stories-heading"
      className="relative py-20 sm:py-28"
    >
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-12 max-w-3xl text-center">
          <p className="text-urgency mb-4 text-xs font-semibold tracking-[0.24em] uppercase">
            Insights
          </p>
          <h2
            id="stories-heading"
            className="font-display text-foreground text-3xl font-semibold tracking-tight text-balance sm:text-4xl"
          >
            Why this work cannot wait
          </h2>
        </div>

        <div
          ref={regionRef}
          role="region"
          aria-roledescription="carousel"
          aria-label="Personal stories"
          tabIndex={0}
          className="border-border bg-card outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background mx-auto max-w-4xl rounded-2xl border p-8 shadow-[0_20px_50px_-40px_rgb(20_23_31_/_0.4)] sm:p-12 lg:p-14"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
              setPaused(false);
            }
          }}
        >
          <figure className="min-w-0 text-center">
            <blockquote>
              <p className="font-display text-foreground text-2xl leading-[1.3] font-medium tracking-tight text-balance sm:text-3xl lg:text-[2.1rem]">
                “{active.quote}”
              </p>
            </blockquote>
            <figcaption className="mt-10">
              <p className="font-display text-foreground text-base font-semibold">{active.name}</p>
              <p className="text-muted-foreground mt-1 text-sm">{active.role}</p>
              {active.storyHref && active.storyLabel ? (
                <Button asChild variant="link" className="text-primary mt-4 h-auto px-0 text-sm">
                  <Link to={active.storyHref}>
                    {active.storyLabel}
                    <span aria-hidden className="ml-1">
                      →
                    </span>
                  </Link>
                </Button>
              ) : null}
            </figcaption>
            <p className="sr-only" aria-live="polite">
              Story {index + 1} of {count}: {active.name}
            </p>
          </figure>

          <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2" role="group" aria-label="Choose story">
              {testimonials.map((item, itemIndex) => {
                const selected = itemIndex === index;
                return (
                  <button
                    key={item.id}
                    type="button"
                    aria-current={selected ? 'true' : undefined}
                    aria-label={`Show story ${itemIndex + 1}: ${item.name}`}
                    className={cn(
                      'h-2.5 rounded-full transition-all',
                      selected
                        ? 'bg-primary w-8'
                        : 'bg-muted-foreground/30 hover:bg-muted-foreground/55 w-2.5'
                    )}
                    onClick={() => goTo(itemIndex)}
                  />
                );
              })}
            </div>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label="Previous story"
                onClick={goPrev}
              >
                <ChevronLeftIcon aria-hidden />
              </Button>
              <Button type="button" variant="outline" size="icon" aria-label="Next story" onClick={goNext}>
                <ChevronRightIcon aria-hidden />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
